import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { apiClient } from '../utils/api';
import { useAuth } from './AuthContext';
import { UserCredits, CreditTransaction, StripeProduct, ApiResponse } from '../types';

interface CreditContextType {
  credits: UserCredits | null;
  transactions: CreditTransaction[];
  transactionsMeta: {
    total: number;
    hasMore: boolean;
    limit: number;
    offset: number;
  };
  availableProducts: StripeProduct[];
  loading: boolean;
  transactionsLoading: boolean;
  fetchCredits: () => void;
  fetchTransactions: (options?: {
    limit?: number;
    offset?: number;
    transactionType?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => void;
  fetchAvailableProducts: () => void;
  purchaseCredits: (productType: string) => Promise<{ success: boolean; url?: string; error?: string }>;
  refreshCredits: () => Promise<void>;
}

const CreditContext = createContext<CreditContextType | undefined>(undefined);

export const useCredits = () => {
  const context = useContext(CreditContext);
  if (context === undefined) {
    throw new Error('useCredits must be used within a CreditProvider');
  }
  return context;
};

export const CreditProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [credits, setCredits] = useState<UserCredits | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [transactionsMeta, setTransactionsMeta] = useState({
    total: 0,
    hasMore: false,
    limit: 20,
    offset: 0
  });
  const [availableProducts, setAvailableProducts] = useState<StripeProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(false);

  const { user } = useAuth();

  const fetchCredits = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const result = await apiClient.get<{ success: boolean; data: UserCredits }>('/credits/balance');
      setCredits(result.data || null);
    } catch (error) {
      console.error('Error fetching credits:', error);
      setCredits(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const fetchTransactions = useCallback(async (options: {
    limit?: number;
    offset?: number;
    transactionType?: string;
    dateFrom?: string;
    dateTo?: string;
  } = {}) => {
    if (!user?.id) return;
    
    setTransactionsLoading(true);
    try {
      const params = new URLSearchParams();
      if (options.limit) params.append('limit', options.limit.toString());
      if (options.offset) params.append('offset', options.offset.toString());
      if (options.transactionType) params.append('transactionType', options.transactionType);
      if (options.dateFrom) params.append('dateFrom', options.dateFrom);
      if (options.dateTo) params.append('dateTo', options.dateTo);

      const response = await apiClient.get<ApiResponse<CreditTransaction[]>>(
        `/credits/transactions?${params.toString()}`
      );
      
      if (response.success && response.data) {
        if (options.offset && options.offset > 0) {
          // Append to existing transactions for pagination
          setTransactions(prev => [...prev, ...response.data!]);
        } else {
          // Replace transactions for new search/filter
          setTransactions(response.data);
        }
        
        // Store pagination metadata
        setTransactionsMeta({
          total: response.meta?.total || 0,
          hasMore: response.meta?.hasMore || false,
          limit: response.meta?.limit || 20,
          offset: response.meta?.offset || 0
        });
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setTransactionsLoading(false);
    }
  }, [user?.id]);

  const fetchAvailableProducts = useCallback(async () => {
    try {
      const result = await apiClient.get<{ success: boolean; data: StripeProduct[] }>('/credits/products');
      setAvailableProducts(result.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      setAvailableProducts([]);
    }
  }, []);

  const purchaseCredits = useCallback(async (productType: string) => {
    try {
      const result = await apiClient.post<{ success: boolean; data: { url: string } }>('/credits/purchase', { productType });
      if (result.success && result.data?.url) {
        window.location.href = result.data.url;
        return { success: true, url: result.data.url };
      } else {
        return { success: false, error: 'Failed to create checkout session' };
      }
    } catch (error) {
      console.error('Error purchasing credits:', error);
      return { success: false, error: 'Purchase failed' };
    }
  }, []);

  const refreshCredits = useCallback(async () => {
    await fetchCredits();
  }, [fetchCredits]);

  return (
    <CreditContext.Provider value={{
      credits,
      transactions,
      transactionsMeta,
      availableProducts,
      loading,
      transactionsLoading,
      fetchCredits,
      fetchTransactions,
      fetchAvailableProducts,
      purchaseCredits,
      refreshCredits
    }}>
      {children}
    </CreditContext.Provider>
  );
}; 